import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { SectionButtons } from '../Button';
import { SectionTitle } from '../Title';
import { ContentContainer } from '../containers';
import {
  DAY_LOWERCASE,
  DAY_PASCAL,
  DAY_TRUNCATED,
  MONTH_LOWERCASE,
  MONTH_PASCAL,
  TVNZ_OPTIMISE
} from '../../constants';
import { VerticalListItem } from '../Lists';
import { useAppDispatch } from '../../redux/hooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { getDayLoading } from '../../services/calculate';
import {
  updateDayLoadingPercentage,
  updateDayTotalLoadingPercentage
} from '../../redux/features/calculated/calculatedSlice';
import {
  formatLoadingDisplay,
  mapPascalMonthToLowercaseMonth,
  mapTruncatedDayToLowercase,
  mapTruncatedDayToPascal,
  truncateLowercaseDay
} from '../../util';

interface DaySelectorProps {
  months: MONTH_PASCAL[] | undefined;
  batchUpdateTotals: (month: MONTH_LOWERCASE, totalDayLoading: number) => void;
}

const DaySelector = ({ months, batchUpdateTotals }: DaySelectorProps) => {
  const dispatch = useAppDispatch();
  const { demographic } = useSelector((state: RootState) => state.detail);
  const { dayStrat } = useSelector((state: RootState) => state.spreadsheet);
  const calculated = useSelector((state: RootState) => state.calculated);

  const [totalLoading, setTotalLoading] = useState<number>(0);
  const [dayLoadingPercentages, setDayLoadingPercentages] = useState<{
    [key: string]: number;
  }>({});
  const [selectedDays, setSelectedDays] = useState<{
    [key in DAY_TRUNCATED]: boolean;
  }>({
    [DAY_TRUNCATED.MON]: true,
    [DAY_TRUNCATED.TUE]: true,
    [DAY_TRUNCATED.WED]: true,
    [DAY_TRUNCATED.THU]: true,
    [DAY_TRUNCATED.FRI]: true,
    [DAY_TRUNCATED.SAT]: true,
    [DAY_TRUNCATED.SUN]: true
  });

  const handleClick = (day: DAY_TRUNCATED, isChecked: boolean): void => {
    const pascalDay = mapTruncatedDayToPascal(day);
    const lowercaseDay = mapTruncatedDayToLowercase(day);

    // set day loading to 0 if the day is checked
    const dayLoading = isChecked
      ? TVNZ_OPTIMISE
      : getDayLoadingPercentageFromSheet(pascalDay);
    const formatted = formatLoadingDisplay(dayLoading);

    setSelectedDays({
      ...selectedDays,
      [day]: isChecked
    });

    setDayLoadingPercentages({
      ...dayLoadingPercentages,
      [day]: isChecked ? TVNZ_OPTIMISE : formatted
    });

    updateReduxForEachMonth(formatted, lowercaseDay);
  };

  const handleToggleAll = (isAllSelected: boolean): void => {
    toggleAll(isAllSelected);
  };

  const toggleAll = (isAllSelected: boolean): void => {
    if (!months) return;

    const lowercaseMonths = months.map(mapPascalMonthToLowercaseMonth);

    toggleAllSelectors(isAllSelected);
    updateReduxLoadingPercentages(isAllSelected, lowercaseMonths);
    setDisplayedLoadingPercentages(isAllSelected, lowercaseMonths);
  };

  const toggleAllSelectors = (isChecked: boolean): void => {
    const toggledDays = Object.entries(DAY_TRUNCATED).map(([_, day]) => [
      day,
      isChecked
    ]);
    setSelectedDays(Object.fromEntries(toggledDays));
  };

  const updateReduxLoadingPercentages = (
    isAllSelected: boolean,
    lowercaseMonths: MONTH_LOWERCASE[]
  ): void => {
    const actions = createActions(isAllSelected, lowercaseMonths);
    actions.forEach((action) => action);
  };

  const setDisplayedLoadingPercentages = (
    isAllSelected: boolean,
    months: MONTH_LOWERCASE[]
  ): void => {
    const updatedDayLoadingPercentages = getUpdatedDayLoadingPercentages(
      isAllSelected,
      months
    );
    setDayLoadingPercentages(updatedDayLoadingPercentages);
  };

  const createActions = (
    isAllSelected: boolean,
    lowercaseMonths: MONTH_LOWERCASE[]
  ): void[] => {
    const selectedAction = isAllSelected
      ? displayZeroLoadingPercentage
      : displayCalculatedLoadingPercentage;

    const actions = lowercaseMonths.flatMap((month: MONTH_LOWERCASE) =>
      Object.values(DAY_TRUNCATED).map((day) => selectedAction(day, month))
    );

    return actions;
  };

  const getUpdatedDayLoadingPercentages = (
    isAllSelected: boolean,
    lowercaseMonths: MONTH_LOWERCASE[]
  ): { [key in DAY_TRUNCATED]: number } => {
    if (isAllSelected) {
      return getFullWeekOptimisedLoadingPercentages();
    }
    return getFullWeekCalculatedLoadingPercentages(lowercaseMonths);
  };

  const getFullWeekOptimisedLoadingPercentages = (): {
    [key in DAY_TRUNCATED]: number;
  } => {
    return Object.fromEntries(
      Object.values(DAY_TRUNCATED).map((day) => [day, TVNZ_OPTIMISE])
    ) as { [key in DAY_TRUNCATED]: number };
  };

  const getFullWeekCalculatedLoadingPercentages = (
    months: MONTH_LOWERCASE[]
  ): {
    [key in DAY_TRUNCATED]: number;
  } => {
    return Object.fromEntries(
      months.flatMap((_) =>
        Object.values(DAY_TRUNCATED).map((day: DAY_TRUNCATED) => {
          const pascalDay = mapTruncatedDayToPascal(day);
          const dayLoadingPercentage =
            getDayLoadingPercentageFromSheet(pascalDay);

          return [day, formatLoadingDisplay(dayLoadingPercentage)];
        })
      )
    ) as { [key in DAY_TRUNCATED]: number };
  };

  const displayZeroLoadingPercentage = (
    day: DAY_TRUNCATED,
    month: MONTH_LOWERCASE
  ): void => {
    const lowercaseDay = mapTruncatedDayToLowercase(day);
    updateReduxDayLoading(TVNZ_OPTIMISE, lowercaseDay, month);
  };

  const displayCalculatedLoadingPercentage = (
    day: DAY_TRUNCATED,
    month: MONTH_LOWERCASE
  ): void => {
    const pascalDay = mapTruncatedDayToPascal(day);
    const lowercaseDay = mapTruncatedDayToLowercase(day);
    const dayLoadingPercentage = getDayLoadingPercentageFromSheet(pascalDay);

    updateReduxDayLoading(
      formatLoadingDisplay(dayLoadingPercentage),
      lowercaseDay,
      month
    );
  };

  const updateReduxDayLoading = (
    dayLoadingPercentage: number,
    day: DAY_LOWERCASE,
    month: MONTH_LOWERCASE
  ): void => {
    const updatedReduxDayLoading = updateDayLoadingPercentage({
      dayLoadingPercentage,
      day,
      month
    });

    dispatch(updatedReduxDayLoading);
  };

  const calculateTotalLoading = (): number => {
    return Object.values(dayLoadingPercentages).reduce(
      (accumulated, current) => accumulated + current,
      TVNZ_OPTIMISE
    );
  };

  const getFormattedTotalLoading = (): number => {
    const totalLoading = calculateTotalLoading() / 100;
    const formattedLoading = formatLoadingDisplay(totalLoading);

    return formattedLoading;
  };

  const getDayLoadingPercentageFromSheet = (day: DAY_PASCAL): number => {
    if (!demographic) {
      return 0;
    }

    return getDayLoading(dayStrat, {
      demographic,
      day
    });
  };

  const updateReduxForEachMonth = (
    dayLoadingPercentage: number,
    day: DAY_LOWERCASE
  ): void => {
    if (!months) return;

    months.map((pascalMonth) => {
      const month = mapPascalMonthToLowercaseMonth(pascalMonth);
      updateReduxDayLoading(dayLoadingPercentage, day, month);
    });
  };

  const updateReduxTotalLoading = (loading: number): void => {
    if (!months) return;

    months.map((pascalMonth) => {
      const month = mapPascalMonthToLowercaseMonth(pascalMonth);
      const newTotalLoading = updateDayTotalLoadingPercentage({
        totalLoading: loading,
        month
      });

      batchUpdateTotals(month, loading);
      dispatch(newTotalLoading);
    });
  };

  const mapInitalState = () => {
    if (!months) return;

    const days = calculated[mapPascalMonthToLowercaseMonth(months[0])].days;

    let toggledDays = {
      ...selectedDays
    };

    let loading = {};

    Object.entries(days).map(([day, value]) => {
      const truncated = truncateLowercaseDay(day as DAY_LOWERCASE);

      if (day === 'loading') return;

      if (value > 0) {
        toggledDays = {
          ...toggledDays,
          [truncated]: false
        };
        loading = {
          ...loading,
          [truncated]: value
        };
      }
    });

    setDayLoadingPercentages({
      ...loading
    });

    setSelectedDays({
      ...toggledDays
    });
  };

  useEffect(() => {
    mapInitalState();
  }, []);

  useEffect(() => {
    const loading = getFormattedTotalLoading();
    setTotalLoading(loading);
    updateReduxTotalLoading(loading);
  }, [selectedDays]);

  return (
    <ContentContainer>
      <SectionTitle title="Days of the week" loadingPercentage={totalLoading} />
      <Box
        display="flex"
        flexDirection="row"
        gap="12px"
        width="100%"
        justifyContent="center"
      >
        {Object.values(DAY_TRUNCATED).map((day: DAY_TRUNCATED) => (
          <VerticalListItem
            key={day}
            title={day}
            isChecked={selectedDays[day]}
            onClick={(isChecked: boolean) => handleClick(day, isChecked)}
            loadingPercentage={dayLoadingPercentages[day] || TVNZ_OPTIMISE}
          />
        ))}
      </Box>
      <Box padding="16px 0px 0px 0px">
        <SectionButtons
          leftButtonLabel="Deselect All"
          rightButtonLabel="Select All"
          onToggleAll={handleToggleAll}
        />
      </Box>
    </ContentContainer>
  );
};

export default DaySelector;
